import { type IssueDetail, ISSUE_TEXT_MAX_LENGTH } from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { CountedTextField, withinLimit } from './CountedTextField.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 「対応した」の小窓。開発しない課題の対応が済んだことを、何をしたか（必須）とともに記録する。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 開発しない課題の対応が済んだことを、何をしたかを書いて記録する小窓。書くまで決定できない
 * @graph-connects useIssueMgmtWrite [calls] markDone
 * @graph-connects useIssueToast [calls] 保存の知らせ
 */
export function DoneDialog({
  issue,
  onClose,
  onDone,
}: {
  issue: IssueDetail;
  onClose: () => void;
  /** 記録した後に呼ぶ（やることのカードを済みにするため） */
  onDone?: (label: string, operationId: string | null) => void;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const markDone = useIssueMgmtWrite('markDone');
  const [note, setNote] = useState('');
  const ready = note.trim() !== '' && withinLimit(note, ISSUE_TEXT_MAX_LENGTH);

  return (
    <Modal
      open
      onClose={onClose}
      title={t('issueMgmt.done.title', { title: issue.title })}
      description={issue.id}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-[var(--color-text-muted)]">
            {ready ? '' : t('issueMgmt.done.hint')}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {t('issueMgmt.common.cancel')}
            </Button>
            <Button
              disabled={!ready}
              loading={markDone.isPending}
              onClick={() => {
                markDone.mutate(
                  { issueId: issue.id, note: note.trim() },
                  {
                    onSuccess: (result) => {
                      onClose();
                      onDone?.(
                        t('issueMgmt.done.saved', { title: issue.title }),
                        result.operationId,
                      );
                      toast.notifySaved(t('issueMgmt.done.saved', { title: issue.title }), {
                        operationId: result.operationId,
                        detail: [
                          t('issueMgmt.done.savedDetail'),
                          ...(result.maskedFields.length > 0 ? [t('issueMgmt.form.masked')] : []),
                        ].join(' ／ '),
                      });
                    },
                    onError: toast.notifyError,
                  },
                );
              }}
            >
              {t('issueMgmt.common.decide')}
            </Button>
          </div>
        </div>
      }
    >
      <CountedTextField
        label={t('issueMgmt.done.note')}
        value={note}
        onChange={setNote}
        max={ISSUE_TEXT_MAX_LENGTH}
        multiline
      />
    </Modal>
  );
}
